import { invertObj, merge, prop, propOr } from 'ramda';

import {
  getRouteKeyForPath,
  getRouteKeyOfChild,
  getUrlForRouteKey,
  isColdPage,
  ROUTE_KEY,
} from '../../config/routes';
import CHILD_ROUTE from '../../config/routes/child-route';
import { REGION_ROUTE_UID } from '../../config/routes/route';
import { DocumentType } from '../../types/document';
import { MEDIA_TYPE } from '../../types/press-media';
import { path, pathOr } from '../compact';
import { getLang } from '../i18n';
import {
  INTERNATIONAL,
  REGION,
  REGION_REGIONAL,
} from './../../types/filters/region';

const deprecatedArticleRoute = ({ lang, uid } = {}) => {
  const lng = getLang(lang);
  if (isColdPage(uid)) {
    const routeKey = getRouteKeyForPath(`/:lng(${lng})/:articleUid(${uid})`);
    return (
      routeKey && {
        routeKey,
        params: { lng: lng, articleUid: uid },
      }
    );
  }
  return {
    routeKey: ROUTE_KEY.ARTICLE,
    params: { lng: lng, articleUid: uid },
  };
};

const hotArticleRoute = ({ lang, uid } = {}) => {
  const lng = getLang(lang);
  return {
    routeKey: ROUTE_KEY.HOT_ARTICLE,
    params: { lng: lng, articleUid: uid },
  };
};

const coldArticleRoute = ({ lang, uid } = {}) => {
  const lng = getLang(lang);
  return {
    routeKey: ROUTE_KEY.COLD_ARTICLE,
    params: { lng: lng, articleUid: uid },
  };
};

const childRoute = (
  paramKey,
  childRouteKey,
  defaultParentRouteKey,
  params = {}
) => ({ uid, lang }, { match: { path } = {} } = {}) => {
  const routeKey = getRouteKeyForPath(path);
  return {
    routeKey: getRouteKeyOfChild(
      defaultParentRouteKey,
      childRouteKey,
      routeKey
    ),
    params: merge({ lng: getLang(lang), [paramKey]: uid }, params),
  };
};

const contactRoute = childRoute(
  'contactUid',
  CHILD_ROUTE.CONTACT.id,
  ROUTE_KEY.CONTACT_PAGE
);

const getDefaultRouteOfMedia = ({ mediaType, onNewsroom, onFinance }) => {
  if (onFinance) {
    return mediaType === MEDIA_TYPE.PUBLICATION
      ? ROUTE_KEY.FINANCIAL_PUBLICATION_YEAR
      : ROUTE_KEY.FINANCIAL_PRESS_RELEASE_YEAR;
  }

  if (onNewsroom) {
    return ROUTE_KEY.NEWSROOM;
  }

  return ROUTE_KEY.NEWSROOM;
};

const getDefaultRouteOfPressRelease = ({ onNewsroom, onFinance }) => {
  if (onFinance) {
    return ROUTE_KEY.FINANCIAL_PRESS_RELEASE_YEAR;
  }

  if (onNewsroom) {
    return ROUTE_KEY.NEWSROOM;
  }

  return ROUTE_KEY.NEWSROOM;
};

const getRouteParamsForMedia = ({ onNewsroom, onFinance, relatedDate }) => {
  if (onFinance) {
    return { year: new Date(relatedDate).getFullYear() };
  }

  if (onNewsroom) {
    return {};
  }

  return {};
};

const getRouteParamsForPressRelease = ({
  onNewsroom,
  onFinance,
  release_date,
}) => {
  if (onFinance) {
    return { year: new Date(release_date).getFullYear() };
  }

  if (onNewsroom) {
    return {};
  }

  return {};
};

const mediaPressRoute = (media, routeInfo) =>
  childRoute(
    'mediaUid',
    CHILD_ROUTE.MEDIA.id,
    getDefaultRouteOfMedia(media),
    getRouteParamsForMedia(media)
  )(media, routeInfo);

const PressReleaseRoute = (pressRelease, routeInfo) =>
  childRoute(
    'pressReleaseUid',
    CHILD_ROUTE.PRESS_RELEASE.id,
    getDefaultRouteOfPressRelease(pressRelease),
    getRouteParamsForPressRelease(pressRelease)
  )(pressRelease, routeInfo);

const DOC_TO_ROUTE = {
  [DocumentType.HOT_ARTICLE]: hotArticleRoute,
  [DocumentType.COLD_ARTICLE]: coldArticleRoute,
  [DocumentType.ARTICLE]: deprecatedArticleRoute,
  [DocumentType.CONTACT]: contactRoute,
  [DocumentType.MEDIA_PRESS]: mediaPressRoute,
  [DocumentType.PRESS_RELEASE]: PressReleaseRoute,
  [DocumentType.NEWSROOM]: () => ({ routeKey: ROUTE_KEY.NEWSROOM }),
  [DocumentType.GOUVERNANCE]: () => ({ routeKey: ROUTE_KEY.GOUVERNANCE }),
  [DocumentType.BRANCH_ACTIVITIES]: () => ({
    routeKey: ROUTE_KEY.BRANCH_ACTIVITIES,
  }),
  [DocumentType.FINANCIAL_DEBT]: () => ({ routeKey: ROUTE_KEY.FINANCIAL_DEBT }),
  [DocumentType.FINANCIAL_RATING]: () => ({
    routeKey: ROUTE_KEY.FINANCIAL_RATING,
  }),
  [DocumentType.FINANCIAL_PUBLICATION]: (doc, routeInfo) => ({
    routeKey:
      path('match.params.year', routeInfo) &&
      path('match.params.year', routeInfo) != new Date(Date.now()).getFullYear()
        ? ROUTE_KEY.FINANCIAL_PUBLICATION_YEAR
        : ROUTE_KEY.FINANCIAL_PUBLICATION,
  }),
  [DocumentType.FINANCIAL_KEY_FIGURES]: () => ({
    routeKey: ROUTE_KEY.FINANCIAL_KEY_FIGURES,
  }),
  [DocumentType.FINANCIAL_PRESS_RELEASE]: (doc, routeInfo) => ({
    routeKey:
      path('match.params.year', routeInfo) &&
      path('match.params.year', routeInfo) != new Date(Date.now()).getFullYear()
        ? ROUTE_KEY.FINANCIAL_PRESS_RELEASE_YEAR
        : ROUTE_KEY.FINANCIAL_PRESS_RELEASE,
  }),
  [DocumentType.HOME]: (doc, routeInfo) => {
    const regionSlug = path('params.regionSlug', routeInfo);
    if (!regionSlug) {
      return {
        routeKey: ROUTE_KEY.HOME,
      };
    }
    return {
      routeKey: ROUTE_KEY.HOME_REGION,
      params: { regionSlug },
    };
  },
  [DocumentType.PUBLIC_SERVICE_MISSIONS]: () => ({
    routeKey: ROUTE_KEY.PUBLIC_SERVICE_MISSIONS,
  }),
  [DocumentType.POSTMEN]: () => ({ routeKey: ROUTE_KEY.POSTMEN }),
  [DocumentType.CONTACT_PAGE]: () => ({ routeKey: ROUTE_KEY.CONTACT_PAGE }),
  [DocumentType.SEARCH_PAGE]: () => ({ routeKey: ROUTE_KEY.SEARCH }),
  [DocumentType.HUB_ACTUS]: () => ({ routeKey: ROUTE_KEY.HUB_ACTU }),
  [DocumentType.HUB_NEWSROOM]: () => ({ routeKey: ROUTE_KEY.HUB_NEWSROOM }),
};

const getRouteKeyFromDocument = (doc, routeInfo) => {
  const docType = prop('type', doc);
  const docToRouteFn = propOr(() => null, docType, DOC_TO_ROUTE);
  return docToRouteFn(doc, routeInfo);
};

export const getUrlFromDocument = (doc, routeInfo, keepFilter = false) => {
  const route = getRouteKeyFromDocument(doc, routeInfo);

  if (!route) {
    return '#';
  }

  const { routeKey, params = {} } = route;
  const lang = getLang(prop('lang', doc));
  const routeParams = pathOr({}, 'match.params', routeInfo);
  return [
    getUrlForRouteKey(routeKey, lang, { ...routeParams, ...params }),
    keepFilter && path('location.search', routeInfo),
  ]
    .filter(Boolean)
    .join('');
};

export const TYPE_TO_ROUTE_REGION = {
  [REGION_REGIONAL.AUVERGNE]: REGION_ROUTE_UID.AUVERGNE,
  [REGION_REGIONAL.BOURGOGNE]: REGION_ROUTE_UID.BOURGOGNE,
  [REGION_REGIONAL.BRETAGNE]: REGION_ROUTE_UID.BRETAGNE,
  [REGION_REGIONAL.CENTRE]: REGION_ROUTE_UID.CENTRE,
  [REGION_REGIONAL.CORSE]: REGION_ROUTE_UID.CORSE,
  [REGION_REGIONAL.DOM]: REGION_ROUTE_UID.DOM,
  [REGION_REGIONAL.GRAND_EST]: REGION_ROUTE_UID.GRAND_EST,
  [REGION_REGIONAL.HAUTS_DE_FRANCE]: REGION_ROUTE_UID.HAUTS_DE_FRANCE,
  [REGION_REGIONAL.ILE_DE_FRANCE]: REGION_ROUTE_UID.ILE_DE_FRANCE,
  [REGION.NATIONAL]: REGION_ROUTE_UID.NATIONAL,
  [REGION_REGIONAL.NORMANDIE]: REGION_ROUTE_UID.NORMANDIE,
  [REGION_REGIONAL.NOUVELLE_AQUITAINE]: REGION_ROUTE_UID.NOUVELLE_AQUITAINE,
  [REGION_REGIONAL.OCCITANIE]: REGION_ROUTE_UID.OCCITANIE,
  [REGION_REGIONAL.PAYS_DE_LA_LOIRE]: REGION_ROUTE_UID.PAYS_DE_LA_LOIRE,
  [REGION_REGIONAL.PACA]: REGION_ROUTE_UID.PACA,
  [INTERNATIONAL]: REGION_ROUTE_UID.INTERNATIONAL,
};

export const routeToTypeRegion = (route) =>
  invertObj(TYPE_TO_ROUTE_REGION)[route];
